import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ScheduledReports = ({ scheduledReports = [] }) => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [filter, setFilter] = useState('all'); // all, active, paused
  const [newSchedule, setNewSchedule] = useState({
    name: '',
    template: '',
    frequency: 'monthly',
    dayOfMonth: 1,
    dayOfWeek: 1,
    time: '09:00',
    recipients: [''],
    format: 'pdf',
    status: 'active'
  });

  const formatNextRun = (dateString) => {
    return new Date(dateString)?.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-success';
      case 'paused': return 'text-warning';
      case 'failed': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return 'Play';
      case 'paused': return 'Pause';
      case 'failed': return 'XCircle';
      default: return 'Circle';
    }
  };

  const getFrequencyIcon = (frequency) => {
    switch (frequency?.toLowerCase()) {
      case 'daily': return 'Calendar';
      case 'weekly': return 'CalendarDays';
      case 'monthly': return 'CalendarRange';
      case 'quarterly': return 'CalendarClock';
      default: return 'Calendar';
    }
  };

  const getFilteredSchedules = () => {
    if (filter === 'all') return scheduledReports;
    return scheduledReports?.filter(schedule => schedule?.status === filter);
  };

  const handleToggleStatus = (scheduleId, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'paused' : 'active';
    console.log(`Toggling schedule ${scheduleId} to ${newStatus}`);
    // Implement status toggle functionality
  };

  const handleRunNow = (scheduleId) => {
    console.log(`Running schedule ${scheduleId} immediately`);
    // Implement run now functionality
  };

  const handleEditSchedule = (scheduleId) => {
    console.log(`Editing schedule ${scheduleId}`);
    // Open edit modal
  };

  const handleDeleteSchedule = (scheduleId) => {
    console.log(`Deleting schedule ${scheduleId}`);
    // Implement delete functionality with confirmation
  };

  const handleCreateSchedule = () => {
    console.log('Creating new schedule:', newSchedule);
    // Implement create functionality
    setShowCreateModal(false);
    setNewSchedule({
      name: '',
      template: '',
      frequency: 'monthly',
      dayOfMonth: 1,
      dayOfWeek: 1,
      time: '09:00',
      recipients: [''],
      format: 'pdf',
      status: 'active'
    });
  };

  const addRecipient = () => {
    setNewSchedule(prev => ({
      ...prev,
      recipients: [...prev?.recipients, '']
    }));
  };

  const removeRecipient = (index) => {
    setNewSchedule(prev => ({
      ...prev,
      recipients: prev?.recipients?.filter((_, i) => i !== index)
    }));
  };

  const updateRecipient = (index, value) => {
    setNewSchedule(prev => ({
      ...prev,
      recipients: prev?.recipients?.map((recipient, i) => 
        i === index ? value : recipient
      )
    }));
  };

  const filteredSchedules = getFilteredSchedules();
  const activeSchedules = scheduledReports?.filter(s => s?.status === 'active')?.length;
  const pausedSchedules = scheduledReports?.filter(s => s?.status === 'paused')?.length;

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-card border border-border rounded-lg p-6 card-shadow">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Calendar" size={20} className="text-primary" />
            </div>
            <div>
              <div className="text-2xl font-bold text-foreground">{scheduledReports?.length}</div>
              <div className="text-sm text-muted-foreground">Total Schedules</div>
            </div>
          </div>
        </div>
        
        <div className="bg-card border border-border rounded-lg p-6 card-shadow">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
              <Icon name="Play" size={20} className="text-success" />
            </div>
            <div>
              <div className="text-2xl font-bold text-foreground">{activeSchedules}</div>
              <div className="text-sm text-muted-foreground">Active</div>
            </div>
          </div>
        </div>
        
        <div className="bg-card border border-border rounded-lg p-6 card-shadow">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center">
              <Icon name="Pause" size={20} className="text-warning" />
            </div>
            <div>
              <div className="text-2xl font-bold text-foreground">{pausedSchedules}</div>
              <div className="text-sm text-muted-foreground">Paused</div>
            </div>
          </div>
        </div>
      </div>
      {/* Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="flex bg-muted rounded-lg p-1">
            <button
              onClick={() => setFilter('all')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                filter === 'all' ?'bg-background text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              All ({scheduledReports?.length})
            </button>
            <button
              onClick={() => setFilter('active')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                filter === 'active' ?'bg-background text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Active ({activeSchedules})
            </button>
            <button
              onClick={() => setFilter('paused')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                filter === 'paused' ?'bg-background text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Paused ({pausedSchedules})
            </button>
          </div>
        </div>
        
        <Button 
          variant="default" 
          iconName="Plus" 
          onClick={() => setShowCreateModal(true)}
        >
          Create Schedule
        </Button>
      </div>
      {/* Scheduled Reports List */}
      <div className="bg-card border border-border rounded-lg card-shadow">
        {filteredSchedules?.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="CalendarX" size={48} className="text-muted-foreground mx-auto mb-4" />
            <h4 className="text-lg font-medium text-foreground mb-2">No Scheduled Reports</h4>
            <p className="text-muted-foreground mb-4">
              {filter === 'all' ? 'Create your first scheduled report' : `No ${filter} schedules found`}
            </p>
            <Button variant="default" iconName="Plus" onClick={() => setShowCreateModal(true)}>
              Create Schedule
            </Button>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {filteredSchedules?.map((schedule) => (
              <div key={schedule?.id} className="p-6 hover:bg-muted/30 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center">
                      <Icon name={getFrequencyIcon(schedule?.frequency)} size={24} className="text-muted-foreground" />
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h4 className="text-lg font-semibold text-foreground">{schedule?.name}</h4>
                        <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full capitalize">
                          {schedule?.frequency}
                        </span>
                        <div className={`flex items-center space-x-1 text-sm ${getStatusColor(schedule?.status)}`}>
                          <Icon name={getStatusIcon(schedule?.status)} size={14} />
                          <span className="capitalize">{schedule?.status}</span>
                        </div>
                      </div>
                      
                      <div className="space-y-1">
                        <div className="flex items-center space-x-6 text-sm text-muted-foreground">
                          <div className="flex items-center space-x-1">
                            <Icon name="Clock" size={14} />
                            <span>Next run: {formatNextRun(schedule?.nextRun)}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <Icon name="Mail" size={14} />
                            <span>{schedule?.recipients?.length} recipient(s)</span>
                          </div>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          Recipients: {schedule?.recipients?.join(', ')}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      iconName="Play"
                      onClick={() => handleRunNow(schedule?.id)}
                    >
                      Run Now
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      iconName={schedule?.status === 'active' ? 'Pause' : 'Play'}
                      onClick={() => handleToggleStatus(schedule?.id, schedule?.status)}
                    >
                      {schedule?.status === 'active' ? 'Pause' : 'Resume'}
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      iconName="Edit"
                      onClick={() => handleEditSchedule(schedule?.id)}
                    >
                      Edit
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      iconName="Trash2"
                      onClick={() => handleDeleteSchedule(schedule?.id)}
                      className="text-error hover:text-error"
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Create Schedule Modal */}
      {showCreateModal && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={() => setShowCreateModal(false)} />
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
            <div className="bg-card border border-border rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto card-shadow">
              <div className="p-6 border-b border-border">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-foreground">Create Scheduled Report</h3>
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className="p-1 hover:bg-muted rounded-md"
                  >
                    <Icon name="X" size={16} />
                  </button>
                </div>
              </div>
              
              <div className="p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="text-sm font-medium text-foreground mb-2 block">Schedule Name</label>
                    <input
                      type="text"
                      value={newSchedule?.name}
                      onChange={(e) => setNewSchedule(prev => ({ ...prev, name: e?.target?.value }))}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="e.g., Monthly Financial Package"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-foreground mb-2 block">Report Template</label>
                    <select
                      value={newSchedule?.template}
                      onChange={(e) => setNewSchedule(prev => ({ ...prev, template: e?.target?.value }))}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      <option value="">Select template...</option>
                      <option value="monthly-financial">Monthly Financial Package</option>
                      <option value="executive-summary">Executive Summary</option>
                      <option value="budget-variance">Budget Variance Report</option>
                      <option value="cash-flow">Cash Flow Analysis</option>
                    </select>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-foreground mb-2 block">Frequency</label>
                    <select
                      value={newSchedule?.frequency}
                      onChange={(e) => setNewSchedule(prev => ({ ...prev, frequency: e?.target?.value }))}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                    </select>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-foreground mb-2 block">Time</label>
                    <input
                      type="time"
                      value={newSchedule?.time}
                      onChange={(e) => setNewSchedule(prev => ({ ...prev, time: e?.target?.value }))}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">Recipients</label>
                  <div className="space-y-2">
                    {newSchedule?.recipients?.map((recipient, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <input
                          type="email"
                          value={recipient}
                          onChange={(e) => updateRecipient(index, e?.target?.value)}
                          className="flex-1 px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                          placeholder="email@company.com"
                        />
                        {newSchedule?.recipients?.length > 1 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            iconName="Trash2"
                            onClick={() => removeRecipient(index)}
                            className="text-error"
                          />
                        )}
                      </div>
                    ))}
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Plus"
                      onClick={addRecipient}
                      className="w-full border-dashed border-2 border-border"
                    >
                      Add Recipient
                    </Button>
                  </div>
                </div>
              </div>

              <div className="p-6 border-t border-border flex items-center justify-end space-x-3">
                <Button variant="outline" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </Button>
                <Button 
                  variant="default" 
                  onClick={handleCreateSchedule}
                  disabled={!newSchedule?.name || !newSchedule?.template}
                >
                  Create Schedule
                </Button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ScheduledReports;